import { cancel, isCancel, note, select } from "@clack/prompts";
import {
  getConfiguredOnboardingDisclaimerAcceptedAt,
  updateConfiguredOnboardingConfig,
} from "../config/runtime-config";
import type { createPresenter } from "../runtime/presenter/index";
import type { RuntimeContext } from "../runtime/types";

export const WAKELOOP_ACCEPT_DISCLAIMER_ENV = "WAKELOOP_ACCEPT_DISCLAIMER";
export const WAKELOOP_ONBOARDING_DISCLAIMER_ACCEPT =
  "__onboarding_disclaimer_accept__";
export const WAKELOOP_ONBOARDING_DISCLAIMER_DECLINE =
  "__onboarding_disclaimer_decline__";
export const WAKELOOP_ONBOARDING_DISCLAIMER_TEXT =
  "WakeLoop local service is experimental and changes rapidly. Breaking changes may occur. You are fully responsible for data security and sensitive information handling. Use WakeLoop only in trusted environments.";

type OnboardingDisclaimerPresenter = ReturnType<typeof createPresenter>;

export async function runOnboardingDisclaimerGate(input: {
  runtime: RuntimeContext;
  presenter: OnboardingDisclaimerPresenter;
  interactive: boolean;
  codePrefix: string;
}): Promise<boolean> {
  const acceptedAt = await getConfiguredOnboardingDisclaimerAcceptedAt().catch(
    () => null
  );
  if (acceptedAt) {
    return true;
  }

  if (shouldBypassDisclaimerWithEnv()) {
    await persistOnboardingDisclaimerAccepted();
    return true;
  }

  if (input.runtime.resolvedView === "agent") {
    input.presenter.data({
      code: input.codePrefix,
      payload: {
        acceptedAt: null,
        text: WAKELOOP_ONBOARDING_DISCLAIMER_TEXT,
        mode: "agent_notice",
        requiresConfirmation: false,
      },
    });
    await persistOnboardingDisclaimerAccepted();
    return true;
  }

  note(`⚠️ ${WAKELOOP_ONBOARDING_DISCLAIMER_TEXT}`, "Disclaimer");
  if (!input.interactive) {
    input.presenter.line({
      code: `${input.codePrefix}.auto_continue`,
      text: "Non-interactive session: disclaimer displayed and WakeLoop continues.",
    });
    await persistOnboardingDisclaimerAccepted();
    return true;
  }

  const decision = await select({
    message: "Do you accept and continue?",
    options: [
      {
        value: WAKELOOP_ONBOARDING_DISCLAIMER_ACCEPT,
        label: "Yes, continue",
      },
      {
        value: WAKELOOP_ONBOARDING_DISCLAIMER_DECLINE,
        label: "No, exit",
      },
    ],
    initialValue: WAKELOOP_ONBOARDING_DISCLAIMER_ACCEPT,
  });

  if (
    isCancel(decision) ||
    decision === WAKELOOP_ONBOARDING_DISCLAIMER_DECLINE
  ) {
    cancel("Cancelled.");
    return false;
  }

  await persistOnboardingDisclaimerAccepted();
  return true;
}

function shouldBypassDisclaimerWithEnv(): boolean {
  return (
    String(process.env[WAKELOOP_ACCEPT_DISCLAIMER_ENV] ?? "").trim() === "1"
  );
}

async function persistOnboardingDisclaimerAccepted(): Promise<void> {
  const acceptedAt = new Date().toISOString();
  await updateConfiguredOnboardingConfig((current) => ({
    ...(current ?? {}),
    disclaimerAcceptedAt: acceptedAt,
  }));
}
